import { createElement } from 'react';
import { HardDriveIcon } from 'lucide-react';

import { defineModule } from '@/shared/registry/types';

import { useDiskCommands } from './commands';
import { DiskSearchResult } from './components/extensions';
import {
  DiskAttachmentPickerEntry,
  FilePanelEntry,
  GroupDiskTabEntry,
  ProfileFilesTabEntry,
} from './entries';
import { setupDiskRealtime } from './realtime';
import { DiskTopBarWidget } from './upload/DiskTopBarWidget';
import { setUploadQueryClient } from './upload/uploadStore';

type PageName =
  | 'MyDrivePage'
  | 'SharedPage'
  | 'CompanyDrivePage'
  | 'GroupDrivesPage'
  | 'GroupDrivePage'
  | 'FolderPage'
  | 'FileRedirectPage'
  | 'TrashPage';

const page = (name: PageName) => async () => ({ Component: (await import('./pages'))[name] });

/**
 * `disk` module (docs/PRODUCT.md "Disk", docs/modules/disk.md): My drive, "Shared with me", the company drive, group
 * drives, folders and the trash (`/disk/...`), the `file:<id>` side panel, the group tab `disk`, the profile tab
 * `files`, "Choose from Disk" for the shared FileUploader, public link pages (`/disk/public/:token`, no sign-in), search
 * hits, ⌘K places and the uploads layer in the top bar.
 */
export const module = defineModule({
  id: 'disk',
  routes: [
    {
      path: 'disk',
      handle: { layout: 'full' },
      lazy: async () => ({ Component: (await import('./pages')).DiskLayout }),
      children: [
        { index: true, lazy: page('MyDrivePage') },
        { path: 'shared', lazy: page('SharedPage') },
        { path: 'company', lazy: page('CompanyDrivePage') },
        { path: 'groups', lazy: page('GroupDrivesPage') },
        { path: 'groups/:groupId', lazy: page('GroupDrivePage') },
        { path: 'folders/:folderId', lazy: page('FolderPage') },
        { path: 'files/:itemId', lazy: page('FileRedirectPage') },
        { path: 'trash', lazy: page('TrashPage') },
      ],
    },
  ],
  publicRoutes: [
    {
      path: 'disk/public/:token',
      lazy: async () => ({ Component: (await import('./pages/PublicLinkPage')).default }),
    },
  ],
  nav: [
    {
      id: 'disk',
      labelKey: 'disk:nav.title',
      icon: HardDriveIcon,
      to: '/disk',
      order: 50,
      section: 'main',
    },
  ],
  sidePanels: [{ type: 'file', component: FilePanelEntry, size: 'xl', titleKey: 'disk:panel.title' }],
  groupTabs: [
    {
      id: 'disk',
      labelKey: 'disk:tabs.group',
      order: 40,
      element: (groupId) => createElement(GroupDiskTabEntry, { groupId }),
    },
  ],
  profileTabs: [
    {
      id: 'files',
      labelKey: 'disk:tabs.profile',
      order: 40,
      element: (userId) => createElement(ProfileFilesTabEntry, { userId }),
    },
  ],
  searchRenderers: { file: DiskSearchResult, folder: DiskSearchResult },
  commands: [{ id: 'disk-places', headingKey: 'disk:palette.heading', order: 50, useItems: useDiskCommands }],
  attachmentSources: [
    {
      id: 'disk',
      labelKey: 'disk:attach.fromDisk',
      icon: HardDriveIcon,
      order: 10,
      picker: DiskAttachmentPickerEntry,
    },
  ],
  topBarWidgets: [{ id: 'disk', order: 30, component: DiskTopBarWidget }],
  setup: (context) => {
    setUploadQueryClient(context.queryClient);
    setupDiskRealtime(context);
  },
});
